import _, {noop, sortBy, take} from 'lodash';
import React, {FC, useEffect, useRef, useState} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {delayAsync} from '../../../src/helpers/asyncHelpers';
import {withExponentialBackoff} from '../../../src/helpers/controlHelpers';
import {useDocumentVisible} from '../../../src/helpers/hooks/focusHookHelpers';
import {findContentUpdatedAt} from '../../../src/helpers/models/conversationHelpers';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  wrapInShallowSelector,
} from '../../../src/helpers/reselectHelpers';
import {ConversationModel} from '../../../src/models/api/conversationModel';
import {MessageModel} from '../../../src/models/api/messageModel';
import {isHttpTooManyRequestsError} from '../../../src/services/http/httpError';
import {reportPrefetch, reportPrefetchNoop} from '../../../src/services/trace';
import {AppState} from '../../../src/store/appState';
import {ResourceMap} from '../../../src/store/appTypes';
import {getConversations, getMessages} from '../../../src/store/data/dataSelectors';
import {getIsUserVisitor, getUserId} from '../../../src/store/data/teammates/teammatesSelectors';
import {getHasOfflineCache} from '../../../src/store/featureFlags/featureFlagsSelectors';
import {CellsPanelPageState, CellsPanelPageTypesEnum} from '../../../src/store/panels/cells/cellsPanelState';
import {
  computeIsAllExceptSelection,
  computeIsIdSelected,
} from '../../../src/store/panels/inboxes/parameters/inboxesParametersHelpers';
import {useConstant, useForceUpdate, useWasUnmounted} from '../../helpers/hookHelpers';
import {buildIndexMap} from '../../helpers/itemIndexHelpers';
import {logger} from '../logger/logger';
import {prefetchConversationAsyncAction} from './offlineConversationActions';
import {setupOfflineConversations} from './offlineConversationCachingHelpers';
import {areOfflineResourcesAvailable} from './offlineResources';
import {useOnline} from './useOnline';

// Skip the prefetching of anything too far from the current selection.
// A distance of 0 would only prefetch the current selection.
const maxConversationDistanceFromSelectionToPrefetch = 10;

/**
 * Non visual component service.
 * This is a component because some data is only available from within the React tree (i.e, the route match)
 */

/* eslint-disable react-hooks/exhaustive-deps */

/*
 * Types.
 */

interface ConversationInfo {
  conversation: ConversationModel;
  contentUpdatedAt: number;
  distanceFromSelection: number;
}

interface OfflineConversationCachingServiceProps {
  /** How long the service should wait before fetching its very first conversation content, after getting mounted. (unit: ms) */
  startDelay: number;
  /** The base delay for the full jitter exponential backoff added before each prefetching call. (unit: ms) */
  baseBackoffDelay: number;
  /** The upper bound for the full jitter exponential backoff added before each prefetching call. (unit: ms) */
  maxBackoffDelay: number;
  /** How long the service should wait after each prefetching call. (unit: ms) */
  cooldownDelay: number;
  prefetchConversationAsync(conversationId: number): Promise<void>;
  cellConversations: ReadonlyArray<ConversationInfo>;
}

interface OfflineConversationCachingComponentProps {
  userId: number;
  isUserVisitor: boolean;
  isOfflineCacheEnabled: boolean;
  prefetchConversationAsync(conversationId: number): Promise<any>;
  cellConversations: ReadonlyArray<ConversationInfo>;
}

interface OfflineConversationCachingContainerProps {
  page: CellsPanelPageState;
}

// Remember items that were recently prefetched so we limit duplicated work and load on the not-always-so-fast indexeddb.
type RecentPrefetching = ReadonlyMap<number, {contentUpdatedAt: number; prefetchedAt: number}>;

/*
 * Component.
 */

const prefetchLogger = logger.getLogger('prefetch');

export const OfflineConversationCachingService: FC<OfflineConversationCachingServiceProps> = (props) => {
  const {baseBackoffDelay, maxBackoffDelay, cooldownDelay, prefetchConversationAsync, cellConversations} =
    props;

  const {wasUnmountedRef} = useWasUnmounted();
  const isPrefetching = useRef(false);
  const recentPrefetching = useRef<RecentPrefetching>(new Map());
  const forceUpdate = useForceUpdate();
  const startDelayPromise = useConstant(() => delayAsync(props.startDelay));

  // Once the infrastructure is ready, see what we can (re) cache everytime the list changes.
  // Stop doing work when we're confident there is nothing left to be cached or a more recent list of conversations is given.
  useEffect(() => {
    if (!areOfflineResourcesAvailable() || isPrefetching.current) {
      return noop;
    }

    recentPrefetching.current = forgetOldPrefetchedItems(recentPrefetching.current);

    const conversationsToPrefetch = computeConversationsToPrefetch(
      cellConversations,
      recentPrefetching.current,
    );

    if (conversationsToPrefetch.length === 0) {
      return noop;
    }

    let didStop = false;

    prefetchNext({
      startDelayPromise,
      baseBackoffDelay,
      maxBackoffDelay,
      cooldownDelay,
      prefetchConversationAsync,
      conversationsToPrefetch,
      index: 0,
      isStopped: () => didStop,
      willPrefetch: () => {
        isPrefetching.current = true;
      },
      didPrefetch: (info, isSuccess) => {
        isPrefetching.current = false;

        reportPrefetch(info.conversation.id, isSuccess);
        addToRecentPrefetching(recentPrefetching, info);

        // There's potentially new work to be done now that we finished this prefetching.
        const wasUnmounted = wasUnmountedRef.current;
        if (didStop && !wasUnmounted) {
          forceUpdate();
        }
      },
    });

    return () => {
      didStop = true;
    };
  }, [cellConversations]);

  return null;
};

/* eslint no-param-reassign: 0 */
function addToRecentPrefetching(
  recentPrefetching: React.MutableRefObject<RecentPrefetching>,
  info: ConversationInfo,
) {
  const newPrefetchInfo = {
    contentUpdatedAt: info.contentUpdatedAt,
    prefetchedAt: Date.now(),
  };

  recentPrefetching.current = new Map([
    ...recentPrefetching.current,
    [info.conversation.id, newPrefetchInfo],
  ]);
}

function forgetOldPrefetchedItems(recentPrefetching: RecentPrefetching) {
  const maxMapSize = 200;

  if (recentPrefetching.size < maxMapSize) {
    return recentPrefetching;
  }

  const newestToOldestEntries = sortBy(Array.from(recentPrefetching), ([, {prefetchedAt}]) => -prefetchedAt);
  const newestEntries = take(newestToOldestEntries, maxMapSize / 2);

  return new Map(newestEntries);
}

function computeConversationsToPrefetch(
  cellConversations: ReadonlyArray<ConversationInfo>,
  recentPrefetching: RecentPrefetching,
): ReadonlyArray<ConversationInfo> {
  return (
    _(cellConversations)
      .filter((info) => {
        const recentPrefetch = recentPrefetching.get(info.conversation.id);

        // We recently fetched this content and it's still fresh, as far as we know.
        if (recentPrefetch && recentPrefetch.contentUpdatedAt === info.contentUpdatedAt) {
          return false;
        }

        // Otherwise, only consider the content for prefetching if it's close to the current selection.
        return info.distanceFromSelection <= maxConversationDistanceFromSelectionToPrefetch;
      })
      // Prioritize prefetching conversations closest to the selected conversation. Prefetch the selected conversation last.
      .sortBy((i) => (i.distanceFromSelection === 0 ? Number.MAX_SAFE_INTEGER : i.distanceFromSelection))
      .value()
  );
}

interface PrefetchNextParams {
  startDelayPromise: Promise<unknown>;
  baseBackoffDelay: number;
  maxBackoffDelay: number;
  cooldownDelay: number;
  conversationsToPrefetch: ReadonlyArray<ConversationInfo>;
  prefetchConversationAsync: (conversationId: number) => Promise<unknown>;
  index: number;
  isStopped: () => boolean;
  willPrefetch: Handler;
  didPrefetch: (info: ConversationInfo, isSuccess: boolean) => void;
}

async function prefetchNext(params: PrefetchNextParams) {
  const {
    startDelayPromise,
    baseBackoffDelay,
    maxBackoffDelay,
    cooldownDelay,
    prefetchConversationAsync,
    conversationsToPrefetch,
    index,
    isStopped,
    willPrefetch,
    didPrefetch,
  } = params;

  if (isStopped()) {
    return;
  }

  const info = conversationsToPrefetch[index];
  if (!info) {
    return;
  }

  await startDelayPromise;

  if (isStopped()) {
    return;
  }

  await withExponentialBackoff(
    async () => {
      if (isStopped()) {
        return;
      }

      try {
        willPrefetch();
        await prefetchConversationAsync(info.conversation.id);
        didPrefetch(info, true);
      } catch (error) {
        // If we were rate limited, retry.
        if (isHttpTooManyRequestsError(error)) {
          throw error;
        }

        didPrefetch(info, false);
      }

      // We just fetched from the server, be nice and wait a bit.
      await delayAsync(cooldownDelay);
    },
    isHttpTooManyRequestsError,
    {
      baseDelay: baseBackoffDelay,
      maxDelay: maxBackoffDelay,
      onComplete: (retryCount, elapsedTime) => {
        if (retryCount === 0) {
          return;
        }

        prefetchLogger.warn('Prefetcher was rate limited', {
          prefetcher: 'visible-timeline',
          retry_count: retryCount,
          duration: elapsedTime,
        });
      },
    },
  );

  if (index === conversationsToPrefetch.length - 1) {
    reportPrefetchNoop();
    prefetchLogger.debug('All conversation contents on current page are already cached');
    return;
  }

  prefetchNext({
    ...params,
    index: index + 1,
  });
}

const OfflineConversationCachingComponent: FC<OfflineConversationCachingComponentProps> = (props) => {
  const {userId, isUserVisitor, isOfflineCacheEnabled} = props;

  const [isOfflineConversationsSetup, setIsOfflineConversationsSetup] = useState(false);
  const isOnline = useOnline();
  const isDocumentVisible = useDocumentVisible();

  // Setup all the necessary infrastructure to use offline mode caching.
  useEffect(() => {
    if (!isOfflineCacheEnabled) {
      return;
    }

    setupOfflineConversations(userId, isUserVisitor).then(setIsOfflineConversationsSetup);
  }, [isOfflineCacheEnabled]);

  if (!isOfflineCacheEnabled || !isOfflineConversationsSetup || !isOnline || !isDocumentVisible) {
    return null;
  }

  return (
    <OfflineConversationCachingService
      startDelay={1000}
      baseBackoffDelay={1000}
      maxBackoffDelay={6 * 60 * 1000}
      cooldownDelay={500}
      prefetchConversationAsync={props.prefetchConversationAsync}
      cellConversations={props.cellConversations}
    />
  );
};

function getPage(_state: AppState, props: OfflineConversationCachingContainerProps) {
  return props.page;
}

function getConversationIdForMessageUid(uid: string, messages: ResourceMap<MessageModel>) {
  const message = messages[uid];

  if (!message) {
    return undefined;
  }

  return message.conversationId;
}

function getConversationById(id: number, conversations: ResourceMap<ConversationModel>) {
  const conversation = conversations[id];

  if (!conversation) {
    return undefined;
  }

  return conversation;
}

const getCellConversations = wrapInShallowSelector(
  createSelector(getPage, getMessages, getConversations, (page, allMessages, allConversations) => {
    // Never prefetch search results.
    if (page.cellsContext.searchQuery) {
      return [];
    }

    const conversationIds =
      page.type === CellsPanelPageTypesEnum.CONVERSATIONS
        ? page.conversationIds
        : _(page.messageUids)
            .map((uid) => getConversationIdForMessageUid(uid, allMessages))
            .compact()
            .value();

    return _(conversationIds)
      .map((id) => getConversationById(id, allConversations))
      .compact()
      .value();
  }),
);

const getCachingInfos = createSelector(
  getCellConversations,
  getUserId,
  getPage,
  (cellConversations, userId, page) => {
    if (page.type !== CellsPanelPageTypesEnum.CONVERSATIONS) {
      return cellConversations.map((c, index) => cachingInfos(c, userId, index, []));
    }

    const {selection} = page.parameters;
    const conversationIds = cellConversations.map((c) => c.id);
    const selectedIndexMap = buildIndexMap(conversationIds);
    const selectedIndexes = computeIsAllExceptSelection(selection)
      ? conversationIds.filter((id) => computeIsIdSelected(selection, id)).map((id) => selectedIndexMap[id])
      : selection.selectedIds.map((id) => selectedIndexMap[id]);

    return cellConversations.map((c, index) => cachingInfos(c, userId, index, selectedIndexes));
  },
);

function cachingInfos(
  conversation: ConversationModel,
  userId: number,
  index: number,
  selectedIndexes: ReadonlyArray<number>,
): ConversationInfo {
  const distanceFromSelection =
    _(selectedIndexes)
      .map((selected) => Math.abs(selected - index))
      .min() || 0;

  return {
    conversation,
    contentUpdatedAt: findContentUpdatedAt(conversation.contentUpdatedDates, userId),
    distanceFromSelection,
  };
}

const mapStateToProps = makeMapStateToProps<OfflineConversationCachingContainerProps>()(() => ({
  userId: getUserId,
  isUserVisitor: getIsUserVisitor,
  isOfflineCacheEnabled: getHasOfflineCache,
  cellConversations: getCachingInfos,
}));

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  prefetchConversationAsync: prefetchConversationAsyncAction,
}));

export const OfflineConversationCachingContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(OfflineConversationCachingComponent);
